import React, {ChangeEvent, FormEvent, useEffect, useState} from 'react';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {VariantType, useSnackbar} from 'notistack';
import {validate} from 'node-cpf';

import {ModalProps} from '@mui/material/Modal/Modal';
import CircularProgress from "@mui/material/CircularProgress";

import LanguageIcon from "@mui/icons-material/Language";
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import {ApiCredencial, ICredResponse} from '../api/ApiCredencial';
import ImgFecomercio from '../../img/fecomercio_rn.png';
import ImgSesc from '../../img/sesc-branco.svg';
import ImgLogin from '../../img/img-login.png';

import CredencialModal from "./CredencialModal";

import InputMask from 'react-input-mask';
function DataInput(props: any) {
    return (
        <InputMask
            mask='99/99/9999'
            value={props.value}
            onChange={props.onChange}
            required={props.required}
            disabled={props.disabled}
            id={props.id}
            placeholder={props.placeholder}
            className='form-control form-control-lg'
        >
        </InputMask>
    );
}


export function ConsultaCartao() {
    return <>
        <div className="container py-5 h-100">
            <div className="row d-flex align-items-center justify-content-center h-100">
                <div className="col-md-8 col-lg-7 col-xl-6">
                    <img src={ImgLogin} className='img-fluid' alt="Sesc Login - Espaço Digital"/>
                </div>
                <div className="col-md-7 col-lg-5 col-xl-5 offset-xl-1">
                <ConsultaCartaoForm/>
                </div>
            </div>
        </div>
    </>;
}

function ConsultaCartaoHeader() {
    return <>
        <div className="card-header">
            <img src={ImgFecomercio} loading="lazy"
                 alt="Fecomercio Logo" width="200px" height="78px"/>
            <img src={ImgSesc} loading="lazy"
                 alt="Sesc Logo" width="200px" height="125px"/>
        </div>
        <hr/>
        <h2>Consulte sua credencial Sesc RN</h2>
    </>;
}

function ConsultaCartaoFooter() {
    return <div className="card-footer">
        <h2><a href="https://www.sescrn.com.br/">
            <LanguageIcon fontSize="small"/> www.sescrn.com.br
        </a></h2>
        <div>
            <a title="Facebook" href="https://www.facebook.com/sescrn">
                <FacebookIcon fontSize="large"/>
            </a>
            <a title="Twitter" href="https://twitter.com/sesc_rn">
                <TwitterIcon fontSize="large"/>
            </a>
            <a title="Instagram" href="https://www.instagram.com/sescrn">
                <InstagramIcon fontSize="large"/>
            </a>
            <a title="Youtube" href="https://www.youtube.com/user/SESCRN">
                <YouTubeIcon fontSize="large"/>
            </a>
            <a title="Whatsapp" href="https://api.whatsapp.com/send?phone=5584981604453">
                <WhatsAppIcon fontSize="large"/>
            </a>
        </div>
    </div>;
}

enum InputStatus {
    VAZIO,
    INVALIDO,
    VALIDO
}

function ConsultaCartaoForm() {
    const
        [loading, setLoading] = useState(false),
        [cpf, setCpf] = useState<string>(''),
        [statusCpf, setStatusCpf] = useState<InputStatus>(InputStatus.VAZIO),
        [nascimento, setNascimento] = useState<string>(''),
        [canSubmit, setCanSubmit] = useState(false),
        [credencial, setCredencial] = useState<ICredResponse>(null),
        [token, setToken] = useState<string>(null),
        {executeRecaptcha} = useGoogleReCaptcha(),
        {enqueueSnackbar} = useSnackbar();

    const callSnackbar = (message: string, variant: VariantType) => {
        enqueueSnackbar(message, {variant, autoHideDuration: 5000})
    }

    const generateToken = () => {
        if (executeRecaptcha)
            executeRecaptcha('cartaodigital')
                .then(setToken)
                .catch(e => {
                    callSnackbar(e?.response?.data?.message || e.message, 'error');
                });
    }

    const refreshToken = () => {
        setToken(null);
        generateToken();
    }

    useEffect(() => {
        if (cpf.length < 1)
            setStatusCpf(InputStatus.VAZIO);
        else if (validate(cpf))
            setStatusCpf(InputStatus.VALIDO);
        else setStatusCpf(InputStatus.INVALIDO);
    }, [cpf]);

    useEffect(() => {
        if ((statusCpf === InputStatus.VALIDO) && (nascimento.length > 0))
            setCanSubmit(true);
        else setCanSubmit(false);
    }, [statusCpf, nascimento]);

    useEffect(() => {
        generateToken();
    }, [executeRecaptcha]);

    const handleCpfChange = (ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setCpf(/\d*/.exec(ev.target.value)[0]);
    };

    const handleNascimentoChange = ({ target: { value } }) => setNascimento(value);


    const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
        const [dia, mes, ano] = nascimento.split('/');
        const formattedNascimento = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;

        ev.preventDefault();
        setLoading(true);
        try {
            const data = await ApiCredencial.get({cpf, token, nascimento: formattedNascimento});
            setCredencial(data);
        } catch (e) {
            callSnackbar(e?.response?.data?.message || e.message, 'error');
            setCredencial(null);
            setNascimento('');
            setCpf('');
            refreshToken();
        } finally {
            setLoading(false);
        }
    }

    const handleModalClose: ModalProps['onClose'] = (event, reason) => {
        if (reason !== 'backdropClick') {
            setCredencial(null);
            setNascimento('');
            setCpf('');
            refreshToken();
        }
    }

    const form = () => {
        if (!token)
            return <div className="card-main loading">
                <CircularProgress title="icone de carregamento"/>
                <span>Aguarde</span>
            </div>;
        return <form className="bg-light p-3 rounded border shadow-sm" onSubmit={handleSubmit}>
            <div
                className="d-flex flex-row align-items-center justify-content-center justify-content-lg-start mt-3 mb-3">
                <p className="lead fw-normal mb-0 me-3 text-center text-black">Consulte sua credencial Sesc RN</p>
            </div>

            <div className="form-outline mb-4">
                <label htmlFor="icpf" className={'form-label'}>CPF *</label>
                <input id="icpf" type="text" placeholder="00000000000" value={cpf} required
                       className={statusCpf === InputStatus.INVALIDO ? 'form-control form-control-lg invalido' : 'form-control form-control-lg'}
                       disabled={loading} onChange={handleCpfChange} maxLength={11}/>
                <small className="text-muted">Somente números.</small>
            </div>
            <div className="form-outline mb-3">
                <label htmlFor="idtn" className="form-label">Data de nascimento *</label>
                <DataInput
                    id={'idtn'}
                    value={nascimento}
                    onChange={handleNascimentoChange}
                    required={true}
                    disabled={loading}
                    placeholder='dd/mm/aaaa'
                >
                </DataInput>
            </div>

            {loading
                ? <CircularProgress/>
                : <>
                    <div className="text-center text-lg-start mt-4 pt-2">
                        <button className={`w-100 btn-lg ${canSubmit ? 'btn-sesc-blue' : 'btn-secondary'}`} type="submit" disabled={!canSubmit}>
                            {loading ?
                                <><i className="fa fa-spinner fa-pulse"> </i> AGUARDE </> :
                                <><i className="fa fa-sign-in"> </i> VERIFICAR </>}
                        </button>
                    </div>
                </>
            }
        </form>;
    }

    return <>
        {credencial && <CredencialModal credencial={credencial} onClose={handleModalClose}/>}
        {form()}
    </>;
}